<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="fixed bottom-20 right-4 z-[9999]">
    <button
      @click="toggleInfo"
      class="bg-[var(--color-primary-200)] hover:bg-[var(--color-primary-300)] active:scale-90 text-[var(--color-primary-900)] rounded-lg p-2 shadow-lg transition-all duration-200 focus:outline-none relative border-2 border-[var(--color-primary-400)]">
      <Info class="w-5 h-5" />
    </button>

    <transition name="fade">
      <div
        v-if="showInfo"
        class="absolute right-0 bottom-full mb-2 w-64 bg-[var(--color-primary-100)] border border-[var(--color-primary-400)] rounded-xl shadow-xl p-4 text-sm text-[var(--color-text)] backdrop-blur-md">
        <div class="flex justify-between items-center mb-2">
          <span class="font-semibold text-[var(--color-primary-900)]">
            {{ infoTitle }}
          </span>
          <X class="w-4 h-4 cursor-pointer" @click="showInfo = false" />
        </div>
        <p class="leading-snug">{{ infoText }}</p>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { Info, X } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'

  const route = useRoute()
  const { t } = useI18n()

  const showInfo = ref(false)
  const toggleInfo = () => (showInfo.value = !showInfo.value)

  // Information or Tutorial for each view/page
  const pageInfo = {
    Home: {
      titleKey: 'views.home.title',
      textKey: 'pageInfo.home',
    },
    GeneralInfo: {
      titleKey: 'views.selectedGoat.generalInfo.title',
      textKey: 'pageInfo.goatdetail.gf',
    },
    GoatList: {
      titleKey: 'views.goats.title',
      textKey: 'pageInfo.goats',
    },
    GoatDetail: {
      titleKey: 'views.selectedGoat.title',
      textKey: 'pageInfo.goatdetail.page',
    },
    GroupDetail: {
      titleKey: 'views.groups.titledetail',
      textKey: 'pageInfo.groupdetail',
    },
    SessionSelect: {
      titleKey: 'views.session.title',
      textKey: 'pageInfo.session',
    },
    SessionGroupSelect: {
      titleKey: 'views.session.title',
      textKey: 'pageInfo.sessionGroupSelect',
    },
    SessionGoatSelect: {
      titleKey: 'views.session.title',
      textKey: 'pageInfo.sessionGoatSelect',
    },
    Temperature: {
      titleKey: 'views.selectedGoat.temperature.title',
      textKey: 'pageInfo.goatdetail.temperature',
    },
    Famacha: {
      titleKey: 'views.selectedGoat.famacha.title',
      textKey: 'pageInfo.goatdetail.famacha',
    },
    GroupList: {
      titleKey: 'views.groups.title',
      textKey: 'pageInfo.groups',
    },
    More: {
      titleKey: 'views.more.title',
      textKey: 'pageInfo.more',
    },
    FocalLengthSettings: {
      titleKey: 'views.more.focalLength.title',
      textKey: 'pageInfo.focalLength',
    },
    Weight: {
      titleKey: 'views.selectedGoat.weight.title',
      textKey: 'pageInfo.goatdetail.weight',
    },
    LegalNotice: {
      titleKey: 'views.more.legalNotice.title',
      textKey: 'views.more.legalNotice.description',
    },
    EartagPrefixSettings: {
      titleKey: 'views.more.eartagPrefix.title',
      textKey: 'views.more.eartagPrefix.description',
    },
    AppInfo: {
      titleKey: 'views.more.appInfo.title',
      textKey: 'views.more.appInfo.description',
    },
    Backup: {
      titleKey: 'views.more.backup.title',
      textKey: 'pageInfo.backup',
    }
  }

  const infoTitle = computed(() => t(pageInfo[route.name]?.titleKey || 'Info'))
  const infoText = computed(() =>
    t(
      pageInfo[route.name]?.textKey || 'No information available for this page.'
    )
  )
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
